/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import api from 'utils/api';
import Input from 'components/Input/Input';
import Magnifier from 'components/Icons/Magnifier/Magnifier';
import './Search.scss';

const callApi = debounce((action, onSuccess, onError) => {
  api(action)
    .then(onSuccess)
    .catch(onError);
}, 300);

/**
 * This is the Search component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Search({
  open,
  action,
  placeholder,
  searching,
  hint,
  match,
  onFocus,
}) {
  const input = useRef(null);
  const [frase, setFrase] = useState('');
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      input.current.focus();
    }
  }, [open]);

  useEffect(() => {
    setLoading(false);
    setError(null);

    if (frase.length > 0) {
      const actionFrase = action.replace('{frase}', frase);
      setLoading(true);
      callApi(
        actionFrase,
        matches => {
          setLoading(false);
          setMatches(matches);
        },
        ({ error }) => {
          setLoading(false);
          setError(error);
        }
      );
    } else {
      setMatches([]);
    }

    return function cleanup() {
      callApi.cancel();
    };
  }, [frase, action]);

  function renderError() {
    if (!error) return null;

    return <strong>{error}</strong>;
  }

  function renderHint() {
    if (matches.length) return null;
    if (loading) return null;

    return (
      <>
        <div className="suggestion-title">{hint.title}</div>
        <ul className="suggestion">
          {hint.suggestion.map((text, i) => (
            <li key={i}>{text}</li>
          ))}
        </ul>
      </>
    );
  }

  function renderMatches() {
    if (!matches.length) return null;
    const title = match.replace(
      '{number}',
      `<strong>${matches.length}</strong>`
    );

    return (
      <>
        <div
          className="matches-title"
          dangerouslySetInnerHTML={{ __html: title }}
        />
        <ul className="matches">
          {matches.map(({ id, type, url, title }) => (
            <li key={id}>
              <div>{type}</div>
              <a tabIndex={open ? null : -1} href={url}>
                {title}
              </a>
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <div
      id="search"
      role="tabpanel"
      aria-labelledby="searchbutton"
      aria-hidden={!open}
      className={classnames('page-padding', 'content-search', { open })}
      onFocus={() => {
        onFocus('search');
      }}
    >
      <Input
        ref={input}
        tabIndex={open ? null : -1}
        className="search-input"
        loading={loading}
        loadingText={searching}
        icon={<Magnifier />}
        value={frase}
        placeholder={placeholder}
        onChange={({ target: { value } }) => {
          setFrase(value);
        }}
      />
      {renderError()}
      {renderHint()}
      {renderMatches()}
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Search.propTypes = {
  action: PropTypes.string.isRequired,
  open: PropTypes.bool,
  searching: PropTypes.string,
  placeholder: PropTypes.string,
  match: PropTypes.string.isRequired,
  hint: PropTypes.shape({
    title: PropTypes.string.isRequired,
    suggestion: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  onFocus: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Search.defaultProps = {
  open: false,
  searching: 'Searching',
  placeholder: 'Enter search here',
  match: '{number} matches',
  onFocus: () => {},
};

export default Search;
